
import DialogMixins from '@/mixins/DialogMixins';
import WidgetMixins from '@/mixins/WidgetMixins';
import { dateFormat, loadWidget } from '@/utils/helpers';
import Component, { mixins } from 'vue-class-component';
import { Getter, namespace } from 'vuex-class';
import { Watch } from 'vue-property-decorator';
import { debounce } from 'lodash';
import moment from 'moment';
import {
  IInvoice,
  IInvoiceQuote,
  InvoiceStatus,
  IPaginate,
  IUser,
} from '@/types/types';
import { printInvoice } from '@/utils/receipts';

const invoicesModule = namespace('invoices');
const authModule = namespace('auth');

@Component({
  name: 'Invoices',
  components: {
    ToolBar: loadWidget('widgets/ToolBar'),
    SnackBar: loadWidget('widgets/SnackBar'),
    EmptyPage: loadWidget('pages/EmptyPage'),
  },
  filters: { dateFormat },
})
export default class Invoices extends mixins(WidgetMixins, DialogMixins) {
  @Getter('getCreditBalance') creditBalance!: number;
  @authModule.Getter('getUserDetails') user!: IUser;
  @invoicesModule.Getter('getInvoices') invoices!: IInvoice[];
  @invoicesModule.Getter('getInvoiceQuote') quote!: IInvoiceQuote | null;
  @invoicesModule.Getter('getPagination') paginate!: {
    page: number;
    pages: number;
    total: number;
    limit: number;
  };

  options: IPaginate = { itemsPerPage: 15, page: 1, total: 0 };
  form: { amount: number | null; recipientMsisdn: string } = {
    amount: null,
    recipientMsisdn: '',
  };

  headers = [
    { text: 'Invoice', value: 'invoiceNumber', sortable: false },
    { text: 'Credits', value: 'credits', sortable: false },
    { text: 'Amount', value: 'amount', sortable: false },
    { text: 'Status', value: 'status', sortable: false },
    { text: 'Issued', value: 'issuedAt', sortable: false },
    { text: 'Due', value: 'dueAt', sortable: false },
    { text: 'Actions', value: 'actions', align: 'center', sortable: false },
  ];

  /** the dialog mixin drives visibility through the store */
  get newInvoiceState(): boolean {
    return this.dialog('newInvoice');
  }
  set newInvoiceState(value: boolean) {
    if (!value) {
      this.close({ idx: 'newInvoice', state: false });
    }
  }

  money(amount: number | string | null | undefined): string {
    return `GH₵ ${Number(amount ?? 0).toLocaleString('en-GH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }

  statusClass(status: InvoiceStatus): string {
    const map: Record<string, string> = {
      DRAFT: 'kx-status--info',
      ISSUED: 'kx-status--info',
      PROCESSING: 'kx-status--pending',
      PAID: 'kx-status--success',
      CANCELLED: 'kx-status--failed',
      EXPIRED: 'kx-status--expired',
    };
    return map[status] ?? 'kx-status--info';
  }

  isPayable(invoice: IInvoice): boolean {
    return ['ISSUED', 'PROCESSING'].includes(invoice.status);
  }

  isOverdue(invoice: IInvoice): boolean {
    return (
      this.isPayable(invoice) &&
      !!invoice.dueAt &&
      moment(invoice.dueAt).isBefore(moment())
    );
  }

  printOne(invoice: IInvoice): void {
    printInvoice(invoice);
  }

  /** priced server-side, so the figure shown is the one that gets issued */
  handleQuote = debounce((amount: number) => {
    this.$store.dispatch('invoices/quote', amount);
  }, 400);

  onAmountChange(value: string): void {
    this.handleQuote(Number(value));
  }

  async issue(): Promise<void> {
    const issued = await this.$store.dispatch('invoices/issue', {
      amount: Number(this.form.amount),
      recipientMsisdn: this.form.recipientMsisdn || undefined,
    });
    if (issued) {
      this.close({ idx: 'newInvoice', state: false });
      this.form = { amount: null, recipientMsisdn: '' };
      this.$store.commit('invoices/UPDATE_QUOTE', null);
      this.loadInvoices();
    }
  }

  /**
   * Hand off to the existing mobile money flow rather than posting a payment
   * from here. That component already does network detection, account-name
   * lookup and status polling; duplicating a second payment path would mean
   * two things to keep correct.
   */
  openPay(invoice: IInvoice): void {
    this.$router
      .push({
        name: 'account.payments',
        query: {
          invoice: `${invoice.id}`,
          msisdn:
            invoice.recipientMsisdn ||
            this.user?.phone ||
            this.user?.mobile ||
            '',
        },
      })
      .catch(() => {
        /* already on the payment page */
      });
  }

  async cancel(invoice: IInvoice): Promise<void> {
    await this.$store.dispatch('invoices/cancel', invoice.id);
    this.loadInvoices();
  }

  loadInvoices(): void {
    this.$store.dispatch('invoices/list', {
      query: `?page=${this.options.page}&limit=${this.options.itemsPerPage}`,
    });
  }

  @Watch('options')
  onOptionsChange(payload: IPaginate): void {
    this.$store.dispatch('invoices/list', {
      query: `?page=${payload.page}&limit=${payload.itemsPerPage}`,
    });
  }

  /**
   * Arriving with ?invoice=<id> (from the Top Up flow) opens that invoice
   * ready to pay, so raising one and paying it is a continuous journey
   * rather than "find the invoice you just made".
   */
  private async openInvoiceFromQuery(): Promise<void> {
    const id = Number(this.$route.query.invoice);
    if (!id) {
      return;
    }
    const invoice = await this.$store.dispatch('invoices/details', id);
    if (
      invoice &&
      invoice.status !== 'PAID' &&
      invoice.status !== 'CANCELLED'
    ) {
      this.openPay(invoice);
    }
    this.$router.replace({ query: {} }).catch(() => {
      /* replacing with the same route is fine */
    });
  }

  created(): void {
    this.$store.dispatch('checkCreditBalance');
    /* load explicitly: the options watcher only fires from the data table,
       which is not rendered while the list is empty, so relying on it alone
       left the page permanently showing the empty state */
    this.loadInvoices();
    this.openInvoiceFromQuery();
  }
}
